import { Form, Input, Select, Spin } from "antd";
import { validateEmail } from "../../Helpers/Validators";
import { getMemberFieldSetting } from "../../Helpers/MemberFieldsHelper";
import { MemberFieldTypes } from "../../Types/MemberFieldTypes";
import { TextService } from "../../Services/TextService";

import { mapDynamicInputLabel } from "../../Mappers/mapDynamicInputLabel";
import { mapDynamicInputName } from "../../Mappers/mapDynamicName";
import { mapDynamicRequiredMessage } from "../../Mappers/mapDynamicRequiredMessage";
import { UI_CONTROLS } from "../../Types/FieldTypes";
import { BookyCheckbox } from "./BookyCheckbox";
import { OneFrontDatePicker } from "./BookyDatePicker";
import { useEffect, useState } from "react";
import { CustomListItemService } from "../../Services/CustomListItemService";
import { PublicSiteService } from "../../Services/PublicSiteService";
import { WebApiCustomListsDtoCustomListItemDto } from "../../Repository/eventbookyapi";
import { useNavigate } from "react-router-dom";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";

interface BookyDynamicInputProps {
  fieldType: any;
  fieldId: number | string | null | undefined;
  memberFieldSettings: any[];
  key?: string | number;
  isAdmin?: boolean;
  validateEmailIfFilledIn?: boolean;
  onChange?: (value: any) => void;
  type?: string;
}

export const BookyDynamicInput = (props: BookyDynamicInputProps) => {
  // Lookup state
  const [lookupOptions, setLookupOptions] = useState<any[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);

  const field = getMemberFieldSetting(props.fieldType, props.memberFieldSettings, props.fieldId);

  const GetText = TextService.GetText;
  const navigation = useNavigate();

  let rules: any = [];
  if (props.isAdmin && field.mandatoryAdmin || !props.isAdmin && field.mandatoryPublic) {
    rules.push({
      required: true,
      message: mapDynamicRequiredMessage(field.columnType!),
    });
  }

  if (props.validateEmailIfFilledIn) {
    rules.push({
      validator: (_: any, value: any) => {
        if (!value || value.length === 0 || validateEmail(value)) {
          return Promise.resolve();
        }
        return Promise.reject(GetText("Common_InvalidEmail"));
      }
    });
  }

  const isPublicUniqueIdField = !props.isAdmin && field.columnType === MemberFieldTypes.UniqueId;
  if (isPublicUniqueIdField) {
    rules = [];
  }


  // Fetch lookup options if needed
  useEffect(() => {
    if (field.uiControl === UI_CONTROLS[4].value && field.customListId) {
      setLookupLoading(true);
      const listId = typeof field.customListId === 'string' ? parseInt(field.customListId, 10) : field.customListId;
      // Staff admin pages and the member self-service portal share this component, but the
      // backend now gates lookup reads separately per audience - route to the matching endpoint.
      const getLookupItems = props.isAdmin
        ? CustomListItemService.GetItems(listId)
        : PublicSiteService.GetCustomListItemsForLookup(listId);
      getLookupItems
        .then((resp: WebApiCustomListsDtoCustomListItemDto[]) => {
          // Try to support both .data and direct array
          setLookupOptions(resp.map((item: WebApiCustomListsDtoCustomListItemDto) => {
            let label = item.fieldValues?.[0].fieldValue;
            return { label, value: item.id };
          }));
        })
        .finally(() => setLookupLoading(false));
    }
  }, [field.uiControl, field.customListId]);

  if (field.visibleInAdmin && props.isAdmin || field.visiblePublic && !props.isAdmin) {
    // Render control based on field.uiControl (intValue)
    switch (field.uiControl) {
      case UI_CONTROLS[4].value: // Lookup
        return (
          <>
            <Form.Item
              label={mapDynamicInputLabel(field.columnType!, props.memberFieldSettings, field.id)}
              name={mapDynamicInputName(field)}
              rules={rules}
            >
              <Select
                loading={lookupLoading}
                options={lookupOptions}
                placeholder={field.helpTextPublic && !props.isAdmin ? field.helpTextPublic : ""}
                onChange={props.onChange}
                allowClear
                showSearch
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
              {lookupLoading && <Spin style={{ marginLeft: 10 }} />}
            </Form.Item>
          </>
        );
      case UI_CONTROLS[2].value: // Checkbox
        return (
          <BookyCheckbox
            label={mapDynamicInputLabel(field.columnType!, props.memberFieldSettings, field.id)}
            name={mapDynamicInputName(field)}
            onChange={props.onChange}
          />
        );
      case UI_CONTROLS[1].value: // DatePicker
        return (
          <OneFrontDatePicker
            label={mapDynamicInputLabel(field.columnType!, props.memberFieldSettings, field.id)}
            name={mapDynamicInputName(field)}
            onChange={props.onChange}
            placeholder={field.helpTextPublic && !props.isAdmin ? field.helpTextPublic : ""}
          />
        );
      case UI_CONTROLS[3].value: // Dropdown
        // Parse alternatives (comma-separated or array)
        let options: string[] = [];
        if (Array.isArray(field.alternatives)) {
          options = field.alternatives;
        } else if (typeof field.alternatives === 'string') {
          options = field.alternatives.split(';').map((s: string) => s.trim()).filter(Boolean);
        }
        return (
          <Form.Item
            label={mapDynamicInputLabel(field.columnType!, props.memberFieldSettings, field.id)}
            name={mapDynamicInputName(field)}
            rules={rules}
          >
            <Select
              data-testid={`${mapDynamicInputName(field)}-select`}
              options={options.map(opt => ({ label: opt, value: opt }))}
              placeholder={field.helpTextPublic && !props.isAdmin ? field.helpTextPublic : ""}
              onChange={props.onChange}
              allowClear
            />
          </Form.Item>
        );
      default:
        // Default to textbox
        return (
          <Form.Item
            label={mapDynamicInputLabel(field.columnType!, props.memberFieldSettings, field.id)}
            name={mapDynamicInputName(field)}
            rules={rules}
            validateTrigger={props.validateEmailIfFilledIn ? 'onBlur' : undefined}
          >
            <Input
              data-testid={`${mapDynamicInputName(field)}-input`}
              type={props.type}
              disabled={isPublicUniqueIdField}
              onChange={props.onChange}
              placeholder={field.helpTextPublic && !props.isAdmin ? field.helpTextPublic : ""}
            />
          </Form.Item>
        );
    }
  }

  return (null);
}